import { useEffect, useMemo } from 'react';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useDataRequest } from 'hooks/useDataRequest';
import { DataResult } from 'models/data';
import { FormattedMessage, useIntl } from 'react-intl';
import { Service, useProDataService } from 'services/data.service';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { CountryInfo, MarketplaceInfo } from 'pages/insights-pro/country-tab/model';
import { ActiveCountries } from 'pages/insights-pro/country-tab/components/ActiveCountries';
import { CountryShares } from 'pages/insights-pro/country-tab/components/CountryShares';
import { TopPerformingCountries } from 'pages/insights-pro/country-tab/components/TopPerformingCountries';
import { MarketplacesCountryShares } from 'pages/insights-pro/country-tab/components/MarketplacesCountryShares';
import { CountriesReturnRates } from 'pages/insights-pro/country-tab/components/CountriesReturnRates';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { useModelService } from 'services/model/model.service';
import { ReportLinkCard } from 'pages/insights-pro/common/reports/ReportLink';
import { FulfillmentMethodSharesChart } from 'pages/insights-pro/common/components/FulfillmentMethodSharesChart';
import { TopGrowthList } from 'pages/insights-pro/common/components/TopGrowthList';
import { SalesPerformanceList } from 'pages/insights-pro/common/components/SalesPerformanceList';
import { ActiveCountriesPerMarketplace } from './components/ActiveCountriesPerMarketplace';
import { CountryDashboardInfoContent } from './components/CountryDashboardInfoContent';

const UNGROUPED_COUNT = 10;

export function CountryDashboard() {
  const { fetchData } = useProDataService(Service.Countries);
  const intl = useIntl();

  const { getMeasures, getAttributes } = useModelService();

  const [chnCountry, chnMarketplace] = getAttributes(['chnCountry', 'chnMarketplace']);

  const [ordGmvVal, ordQty, ordNum] = getMeasures(['ordGmvVal', 'ordQty', 'ordNum']);

  const requestData = useDataRequest();
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const countryDashboardInfo = {
      titleKey: 'Country Dashboard',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CountryDashboardInfoContent />,
    };

    setContent(countryDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const extractCountryInfo = (data: DataResult) => {
    const countries: CountryInfo[] = data.sets.countries.map(entry => ({
      code: chnCountry.getRawValue(entry.attributes)?.toLowerCase().trim(),
      name: chnCountry.getValue(entry.attributes),
      color: '',
      isOther: false,
      entry,
    }));

    countries.sort((a, b) => ordGmvVal.getValue(b.entry.values) - ordGmvVal.getValue(a.entry.values));

    countries.forEach((_, i) => {
      countries[i].color = getChartColorWithMax(i, UNGROUPED_COUNT);
      if (i >= UNGROUPED_COUNT - 1) {
        countries[i].isOther = true;
      }
    });

    return countries;
  };
  const extractMarketPlacesFromCountries = (countries: CountryInfo[]) => {
    const marketplaces: MarketplaceInfo[] = [];

    countries.forEach(country => {
      country.entry.sets.marketplaces.forEach(countryMarketplace => {
        const marketplace =
          marketplaces.find(m => m.name === chnMarketplace.getValue(countryMarketplace.attributes)) ??
          (() => {
            const newMarkeplace = {
              name: chnMarketplace.getValue(countryMarketplace.attributes),
              totalGmv: 0,
              countries: [],
            };
            marketplaces.push(newMarkeplace);
            return newMarkeplace;
          })();

        marketplace.totalGmv += ordGmvVal.getValue(countryMarketplace.values);
        marketplace.countries.push({ ...country, entry: countryMarketplace });
      });
    });
    marketplaces.sort((a, b) => b.totalGmv - a.totalGmv);
    return marketplaces;
  };

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );

      const countries = extractCountryInfo(response);
      const marketplaces = extractMarketPlacesFromCountries(countries);

      return {
        isComparisonActive,
        data: response,
        countryCountCompare: isComparisonActive
          ? response.sets.countries.filter(country => ordQty.getValue(country.compareValues) >= 0).length
          : null,
        countries,
        marketplaces,
      };
    },
    ['insights-countries-dashboard']
  );

  const avgSoldItemsPerProdTitle = useMemo(
    () =>
      intl.formatMessage(
        { id: 'general.title.by-metric' },
        {
          metric: intl.formatMessage({
            id: 'model.calculated.metric.avgSoldItemsPerProduct.name',
          }),
        }
      ),
    [intl]
  );

  if (!dashboardState.data && !dashboardState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const byGmvTitle = intl.formatMessage(
    {
      id: 'general.title.by-metric',
    },
    {
      metric: ordGmvVal.metric.name,
    }
  );

  if (dashboardState.isError) {
    return (
      <PageContainer>
        <Tile
          titleId="insights.country"
          errorDetails={dashboardState.error}
          testId="insights-countries-dashboard-error"
        />
      </PageContainer>
    );
  }

  const dashboardData = dashboardState.data!;

  return (
    <PageContainer>
      <Tile className="xl:col-span-6" titleId="insights.country_tab.top_performing_header" additionalTitle={byGmvTitle}>
        <TopPerformingCountries
          countries={dashboardData.countries}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
          qtyMeasure={ordQty}
        />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.country_tab.country_share_gmv_header"
        additionalTitle={byGmvTitle}
      >
        <CountryShares
          countries={dashboardData.countries}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
      <ReportLinkCard
        className="xl:col-span-6"
        titleId="insights.country.reports.channel_breakdown"
        descriptionId="insights.country.reports.channel_breakdown.description"
        linkUrl="reports/channel-breakdown"
      />
      <ReportLinkCard
        className="xl:col-span-6"
        titleId="insights.country.reports.over_time"
        descriptionId="insights.country.reports.over_time.description"
        linkUrl="reports/over-time"
      />

      <Tile
        titleId="insights.country_tab.top_growing_header"
        additionalTitle={intl.formatMessage({ id: 'insights.measure.orderedGMVGrowth.by' })}
        className="xl:col-span-6"
      >
        <TopGrowthList
          items={dashboardData.countries}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
          getNameCellProps={item => ({
            name: item.name,
            countryCode: item.code,
            color: item.color,
          })}
        />
      </Tile>
      <Tile
        titleId="insights.country_tab.country_return_rates_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.country_tab.country_return_rates_header_subheader',
        })}
        className="xl:col-span-6"
      >
        <CountriesReturnRates
          total={dashboardData.data}
          countries={dashboardData.countries}
          isComparisonActive={dashboardData.isComparisonActive}
        />
      </Tile>
      <Tile
        titleId="insights.country_tab.country_share_marketplace_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.country_tab.country_share_marketplace_header_subheader',
        })}
        className="xl:col-span-6"
      >
        <MarketplacesCountryShares
          marketplaces={dashboardData.marketplaces}
          countries={dashboardData.countries}
          gmvMeasure={ordGmvVal}
        />
      </Tile>

      <Tile
        titleId="insights.products.products_sold_by_countries_header"
        additionalTitle={avgSoldItemsPerProdTitle}
        className="xl:col-span-6"
      >
        <SalesPerformanceList
          attribute={chnCountry}
          isComparisonActive={dashboardData.isComparisonActive}
          entries={dashboardData.countries.map(c => c.entry)}
          showFlag
        />
      </Tile>

      <Tile
        className="xl:col-span-6"
        titleId="insights.country_tab.active_countries_header"
        additionalTitle={intl.formatMessage(
          {
            id: 'general.title.by-metric',
          },
          {
            metric: ordNum.metric.name,
          }
        )}
      >
        <ActiveCountries
          countries={dashboardData.countries}
          isComparisonActive={dashboardData.isComparisonActive}
          qtyMeasure={ordQty}
        />
      </Tile>
      <Tile
        titleId="fulfillment.method.fulfillment_header"
        additionalTitle={byGmvTitle}
        className="row-span-2 xl:col-span-6"
      >
        <FulfillmentMethodSharesChart
          data={dashboardData.countries}
          name={c => c.name}
          fulfillmentMethods={c => c.entry.sets?.fulfillmentMethods || []}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.country_tab.active_countries_header"
        additionalTitle={<FormattedMessage id="insights.country_tab.active_countries_per_marketplace" />}
      >
        <ActiveCountriesPerMarketplace
          marketplaces={dashboardData.marketplaces}
          qtyMeasure={ordQty}
          gmvMeasure={ordGmvVal}
        />
      </Tile>
    </PageContainer>
  );
}
